import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import { adminApi } from '../adminApi';

interface AdminDeviceRow {
  id: string;
  manufacturer_code: number | null;
  manufacturer_name: string | null;
  product_code: number | null;
  model_id: string | null;
  device_class_name: string | null;
  device_function_name: string | null;
  // Resolved catalog name (ADR 0052): admin preferred_name → inferred
  // `model_version (function)`. `preferred_name` is the override alone.
  preferred_name: string | null;
  display_name: string;
  user_count: number;
  boat_count: number;
  // Count of PUBLISHED manuals attached to this model (suggested/rejected
  // excluded). 0 = no manual. Drives the Manual column's icon link.
  manual_count: number;
}

function manufacturerLabel(d: AdminDeviceRow): string {
  if (d.manufacturer_name !== null) return d.manufacturer_name;
  if (d.manufacturer_code !== null) return `#${d.manufacturer_code}`;
  return '—';
}

function modelLabel(d: AdminDeviceRow): string {
  if (d.model_id !== null) return d.model_id;
  if (d.product_code !== null) return `Product ${d.product_code}`;
  return 'Unknown model';
}

// Feather/Lucide "file-text" — a document with text lines = "this model has a
// manual". 24×24, stroke=currentColor so it inherits the teal link color and
// hover/focus states. aria-hidden — the wrapping link carries the label.
function FileTextIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8Z" />
      <path d="M14 2v6h6" />
      <path d="M16 13H8" />
      <path d="M16 17H8" />
      <path d="M10 9H8" />
    </svg>
  );
}

// The Manual column cell. A model with ≥1 published manual gets a teal
// file-text icon linking to its detail page's manuals section; a model with
// none gets a muted dash. The icon-only link carries its accessible name on
// the <a> (the svg is aria-hidden); a small count rides alongside when >1.
function ManualCell({ d }: { d: AdminDeviceRow }) {
  if (d.manual_count <= 0) {
    return (
      <span className="admin-manual-none" aria-hidden="true" title="No manual">
        —
      </span>
    );
  }
  const noun = d.manual_count === 1 ? 'manual' : 'manuals';
  return (
    <Link
      to={`/admin/devices/${d.id}#manuals`}
      className="admin-manual-link"
      aria-label={`View ${d.manual_count} ${noun} for ${d.display_name}`}
      title={`${d.manual_count} ${noun}`}
    >
      <FileTextIcon />
      {d.manual_count > 1 ? (
        <span className="admin-manual-count">{d.manual_count > 9 ? '9+' : d.manual_count}</span>
      ) : null}
    </Link>
  );
}

export function AdminDevices() {
  const [devices, setDevices] = useState<AdminDeviceRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    void (async () => {
      try {
        const res = await adminApi<{ devices: AdminDeviceRow[] }>('/v1/admin/devices');
        setDevices(res.devices);
      } catch (err) {
        const e = err as { body?: { message?: string } };
        setError(e.body?.message ?? 'Failed to load devices.');
      }
    })();
  }, []);

  return (
    <section className="admin-section">
      <header className="admin-section-header">
        <h1>Devices</h1>
        <span className="admin-count">{devices === null ? '…' : `${devices.length} models`}</span>
      </header>

      {error !== null ? <div className="admin-auth-banner error">{error}</div> : null}

      {devices === null && error === null ? <p className="admin-loading-row">Loading…</p> : null}

      {devices !== null ? (
        devices.length === 0 ? (
          <p className="admin-empty">No devices identified yet.</p>
        ) : (
          <div className="scroll-x">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Manufacturer</th>
                  <th>Model</th>
                  <th>Manual</th>
                  <th>Users</th>
                  <th>Boats</th>
                </tr>
              </thead>
              <tbody>
                {devices.map((d) => (
                  <tr key={d.id} data-testid={`admin-device-row-${d.id}`}>
                    <td>
                      <Link to={`/admin/devices/${d.id}`}>{d.display_name}</Link>
                    </td>
                    <td>{manufacturerLabel(d)}</td>
                    <td>{modelLabel(d)}</td>
                    <td data-testid={`admin-device-manual-${d.id}`}>
                      <ManualCell d={d} />
                    </td>
                    <td>{d.user_count}</td>
                    <td>{d.boat_count}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )
      ) : null}
    </section>
  );
}
